import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { QuestionnaireRequest } from 'src/API';
import { ConsentSendService } from 'src/app/core/api/consent-send.service';
import { SendQuestionnaireDialogData } from 'src/app/core/api/questionnaire-request.service';
import { SideNavActionsService } from 'src/app/core/api/sidenav-actions.service';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { PatientConsentStatusService } from 'src/app/core/services/patient-consent-status.service';
import { StaffPermissionsService } from 'src/app/core/services/staff-permissions.service';
import {
  CLINIC_SETUP_QUESTIONNAIRE_TAB,
  CLINIC_SETUP_TIMING_AND_ALERTS_SUB_TAB
} from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup-modal.component';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { SendQuestionnaireDialogComponent } from 'src/app/shared/send-questionnaire-dialog/send-questionnaire-dialog.component';
import { SideSheetService } from 'src/app/shared/side-sheet/side-sheet.service';
import { DeclinedConsentRow } from './declined-consent-pill.component';

/**
 * Drop-in declined-consent warning for a patient: connects consent status to the pill and handles
 * resending, so host screens only need `<csi-patient-declined-consents [patientId]>`.
 */
@Component({
  selector: 'csi-patient-declined-consents',
  template: `
    <csi-declined-consent-pill
      [rows]="rows$ | async"
      [canResend]="staffPermissionsService.permissions.canSendQuestionnaires"
      [canManageWarnings]="staffPermissionsService.permissions.canManageClinicSettings"
      (resendConsent)="resendConsent($event)"
      (manageWarnings)="openConsentWarningSettings()"
    ></csi-declined-consent-pill>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PatientDeclinedConsentsComponent implements OnChanges {
  @Input() patientId: string;

  public rows$: Observable<DeclinedConsentRow[]> = of([]);

  constructor(
    public staffPermissionsService: StaffPermissionsService,
    private patientConsentStatusService: PatientConsentStatusService,
    private consentSendService: ConsentSendService,
    private clinicSetupService: ClinicSetupService,
    private appStateService: AppStateService,
    private sideSheetService: SideSheetService,
    private sideNavActionsService: SideNavActionsService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.patientId) {
      const declinedRows$ = this.patientConsentStatusService
        .getDeclinedConsentFormResponsesForPatient(this.patientId)
        .pipe(
          map(responses =>
            responses.map(response => ({
              response,
              sendKey: this.consentSendService.findConsentFormKey(response)
            }))
          )
        );
      this.rows$ = combineLatest([
        this.clinicSetupService.declinedConsentWarningsEnabled$,
        declinedRows$
      ]).pipe(map(([warningsEnabled, rows]) => (warningsEnabled ? rows : [])));
    }
  }

  // Rows only exist while app state holds this patient, so its fully loaded record (email, phone,
  // clinic) is what the send dialog needs.
  public resendConsent(row: DeclinedConsentRow) {
    const patient = this.appStateService.getCurrentPatient();
    if (!row.sendKey || !patient || patient.id !== this.patientId) {
      return;
    }
    this.sideSheetService.open<
      SendQuestionnaireDialogComponent,
      SendQuestionnaireDialogData,
      QuestionnaireRequest | QuestionnaireRequest[]
    >(
      SendQuestionnaireDialogComponent,
      SendQuestionnaireDialogComponent.buildDialogData(null, patient, null, true, true, [
        row.sendKey
      ])
    );
  }

  public openConsentWarningSettings() {
    this.sideNavActionsService.openClinicSetupModal({
      selectedTabLabel: CLINIC_SETUP_QUESTIONNAIRE_TAB,
      selectedSubTabLabel: CLINIC_SETUP_TIMING_AND_ALERTS_SUB_TAB
    });
  }
}
